import React, { useEffect, useState } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState } from "draft-js";
import {
  CloseButton,
  SaveButton,
} from "../../components/common/MainButtonComponent";
import SectionSpinner from "../../components/Spinner2";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./AssessSections.css";
import * as assessSectionsService from "../../service/assessSections/assessSectionsService/assessSectionsService";
import DataHandler from "../../handlers/DataHandler";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { use } from "react";
import { sweetSuccessAlert, sweetErrorAlert } from "../../components/common/SweetAlert";

// const durationUnitOptions = [
//   { value: "1", label: "minutes" },
//   { value: "2", label: "hours" },
//   { value: "3", label: "days" },
//   { value: "4", label: "weeks" },
//   { value: "5", label: "months" },
// ];

const SelectField = ({ label, required, value, options = [], selectPlaceholder, disabled, name, onChange, error, }) => (
  <div className="assess-setup__field">
    <label>
      {label}
      {required ? "*" : ""}
    </label>
    <select
      name={name}
      value={value || ""}
      onChange={onChange}
      disabled={disabled}
    >
      <option value="">{selectPlaceholder}</option>
      {options.map((option, index) => {
        const normalized =
          option && typeof option === "object"
            ? {
              value: String(option.value ?? ""),
              label: String(option.label ?? ""),
            }
            : { value: String(option ?? ""), label: String(option ?? "") };

        return (
          <option
            key={`${name || label}-${normalized.value}-${index}`}
            value={normalized.value}
          >
            {normalized.label}
          </option>
        );
      })}
    </select>
    {error && <div className="assess-setup__field-error">{error}</div>}
  </div>
);

const TextField = ({ label, required, value, type = "text", disabled, placeholder = "", name, onChange, error, }) => (
  <div className="assess-setup__field">
    <label>
      {label}
      {required ? "*" : ""}
    </label>
    <input
      type={type}
      value={value || ""}
      disabled={disabled}
      placeholder={placeholder}
      name={name}
      onChange={onChange}
    />
    {error && <div className="assess-setup__field-error">{error}</div>}
  </div>
);

const CheckboxPair = ({ label, value = false, disabled, name, onChange }) => (
  <div className="assess-setup__check-row">
    <label className="assess-setup__check-label">{label}</label>
    <label className="assess-setup__check-choice">
      <span>Yes</span>
      <input
        type="radio"
        name={name}
        checked={Boolean(value)}
        onChange={() => onChange(true)}
        disabled={disabled}
      />
    </label>
    <label className="assess-setup__check-choice">
      <span>No</span>
      <input
        type="radio"
        name={name}
        checked={!Boolean(value)}
        onChange={() => onChange(false)}
        disabled={disabled}
      />
    </label>
  </div>
);

const TermsEditor = ({ value, onChange, readOnly }) => {
  return (
    <div className="assess-editor">
      <ReactQuill
        theme="snow"
        value={value || ""}
        onChange={onChange}
        readOnly={readOnly}
      />
    </div>
  );
};

export default function AssessmentSetupForm({
  mode = "create",
  values = {},
  metaData: metaDataOverride = null,
  metadataOptionsOverride = null,
  submitLabel = "Create",
  onClose,
  onSubmit,
}) {
  const isView = mode === "view";
  const sessionUserId = String(DataHandler.getFromSession("user_id") || "");

  const [tempData, setTempData] = useState({
    privilege_status: "Public",
  });

  const [formValues, setFormValues] = useState({
    title: "",
    assessment_state: "Initial",
    status_id: 1,

    assessment_admin: sessionUserId,
    created_by: sessionUserId,
    author_id: null,
    parent_assessment_id: null,

    exam_category_id: null,
    grade_id: null,
    subject_id: null,
    language_id: null,
    difficulty_id: null,
    category_id: null,
    chapter_id: null,
    source_id: null,

    grading_method_id: 3,
    attempt_no_id: 1,
    "no_of_parts": 1,
    "part_notation_id": null,
    // "template_id": null,
    privilege_id: 1,

    ismaster: true,
    is_multiple: false,
    is_pause: false,
    is_restart: false,
    proctor_requirement: false,

    pause_until: 0,

    additional_info: "",
    terms_content: "",

    term_id: null,
    year_id: null,

    // "category_id": null,
    // "teacher_id": null,
    // "data_entry_id": "",
  });

  const [schedule, setSchedule] = useState({
    start_time: "",
    exam_time_interval: "",
    buffer_time: "",
    scheduled_by: sessionUserId,
    exam_time_interval_unit_id: "",
    buffer_time_unit_id: "",
    pause_duration_unit_id: "",
    is_set_time_limit: false,

  });

  const [assessors, setAssessors] = useState({
    user_id: sessionUserId,
    chief_assessor: true,
    assigned_by: sessionUserId,
  });

  const [userRoles, setUserRoles] = useState({
    user_id: sessionUserId,
    role_code: "ASAD",
    assigned_by: sessionUserId,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [termsExpanded, setTermsExpanded] = useState(!isView);

  useEffect(() => {
    if (!values || Object.keys(values).length === 0) {
      return;
    }


    const scheduleKeys = [
      "start_time",
      "exam_time_interval",
      "exam_time_interval_unit_id",
      "buffer_time",
      "buffer_time_unit_id",
      "is_set_time_limit",
      "scheduled_by",
      "pause_duration",
      "pause_duration_unit_id",
      "assessment_id",
      "parent_assessment_title"

    ];

    const incomingAssessment = { ...values };
    const incomingSchedule = {};
    scheduleKeys.forEach((k) => {
      if (k in incomingAssessment) {
        incomingSchedule[k] = incomingAssessment[k];
        delete incomingAssessment[k];
      }
    });


    setFormValues((prev) => ({
      ...prev,
      ...incomingAssessment,
      title: incomingAssessment.title ?? prev.title,
      assessment_state: incomingAssessment.assessment_state ?? prev.assessment_state,
      status_id: incomingAssessment.status_id ?? prev.status_id,
      grade_id: incomingAssessment.grade_id ?? prev.grade_id,
      subject_id: incomingAssessment.subject_id ?? prev.subject_id,
      language_id: incomingAssessment.language_id ?? prev.language_id,
      exam_category_id: incomingAssessment.exam_category_id ?? prev.exam_category_id,
      term_id: incomingAssessment.term_id ?? prev.term_id,
      author_id: incomingAssessment.author_id ?? prev.author_id,
      source_id: incomingAssessment.source_id ?? prev.source_id,
      grading_method_id: incomingAssessment.grading_method_id ?? prev.grading_method_id,
      chapter_id: incomingAssessment.chapter_id ?? prev.chapter_id,
      year_id: incomingAssessment.year_id ?? prev.year_id,
      difficulty_id: incomingAssessment.difficulty_id ?? prev.difficulty_id,
      attempt_no_id: incomingAssessment.attempt_no_id ?? prev.attempt_no_id,
      additional_info: incomingAssessment.additional_info ?? prev.additional_info,
      terms_content: incomingAssessment.terms_content ?? prev.terms_content,
      no_of_parts: incomingAssessment.no_of_parts ?? prev.no_of_parts,
      part_notation_id: incomingAssessment.part_notation_id ?? prev.part_notation_id,
      parent_assessment_id:
        incomingAssessment.parent_assessment_id === undefined
          ? prev.parent_assessment_id
          : incomingAssessment.parent_assessment_id,
      is_pause: incomingAssessment.is_pause === undefined ? prev.is_pause : Boolean(incomingAssessment.is_pause),
      is_restart:
        incomingAssessment.is_restart === undefined ? prev.is_restart : Boolean(incomingAssessment.is_restart),
      is_multiple:
        incomingAssessment.is_multiple === undefined ? prev.is_multiple : Boolean(incomingAssessment.is_multiple),
      ismaster: incomingAssessment.ismaster === undefined ? prev.ismaster : Boolean(incomingAssessment.ismaster),
    }));

    // Normalize schedule values and merge into schedule state


    setSchedule((prev) => ({
      ...prev,
      start_time: incomingSchedule.start_time ?? prev.start_time,
      exam_time_interval: incomingSchedule.exam_time_interval ?? prev.exam_time_interval,
      buffer_time: incomingSchedule.buffer_time ?? prev.buffer_time,
      exam_time_interval_unit_id:
        incomingSchedule.exam_time_interval_unit_id ?? prev.exam_time_interval_unit_id,
      buffer_time_unit_id: incomingSchedule.buffer_time_unit_id ?? prev.buffer_time_unit_id,
      is_set_time_limit:
        incomingSchedule.is_set_time_limit === undefined
          ? prev.is_set_time_limit
          : Boolean(incomingSchedule.is_set_time_limit),
      pause_duration: incomingSchedule.pause_duration ?? prev.pause_duration,
      pause_duration_unit_id:
        incomingSchedule.pause_duration_unit_id ?? prev.pause_duration_unit_id,
      scheduled_by: incomingSchedule.scheduled_by ?? prev.scheduled_by,
    }));
  }, [values]);

  useEffect(() => {
    setTermsExpanded(!isView);
  }, [isView]);


  //--------------------------
  //meta data states handling
  //--------------------------
  const [metaData, setMetaData] = useState(metaDataOverride);
  const [metadataOptions, setMetadataOptions] = useState(metadataOptionsOverride);
  const [isMetaDataLoading, setIsMetaDataLoading] = useState(false);
  const [metaDataError, setMetaDataError] = useState(null);
  const isFormDisabled = isView || isMetaDataLoading;

  useEffect(() => {
    if (metaDataOverride) {
      setMetaData(metaDataOverride);
    }
  }, [metaDataOverride]);

  useEffect(() => {
    if (metadataOptionsOverride) {
      setMetadataOptions(metadataOptionsOverride);
    }
  }, [metadataOptionsOverride]);

  // useEffect(() => {
  //   if (metaDataOverride) {
  //     return;
  //   }

  //   const fetchMetaData = async () => {
  //     try {
  //       setIsMetaDataLoading(true);
  //       setMetaDataError(null);
  //       const response = await assessSectionsService.getMetaDataForFilter();
  //       setMetaData(response);
  //     } catch (error) {
  //       setMetaDataError(error);
  //     } finally {
  //       setIsMetaDataLoading(false);
  //     }
  //   };
  //   fetchMetaData();
  // }, []);

  const getMetaDataCollection = (metaDataPayload, sectionName) => {
    if (!metaDataPayload) {
      return [];
    }
    if (Array.isArray(metaDataPayload?.[sectionName])) {
      return metaDataPayload[sectionName];
    }
    if (Array.isArray(metaDataPayload?.data)) {
      const section = metaDataPayload.data.find((item) =>
        Array.isArray(item?.[sectionName]),
      );
      return section?.[sectionName] || [];
    }
    return [];
  };

  const mapOptions = (dataArray, idKey, labelKeys = []) =>
    (dataArray || [])
      .map((item) => {
        const labelKey = labelKeys.find(
          (key) => item?.[key] !== undefined && item?.[key] !== null,
        );
        if (
          !item ||
          item[idKey] === undefined ||
          item[idKey] === null ||
          !labelKey
        ) {
          return null;
        }
        return {
          value: String(item[idKey]),
          label: String(item[labelKey]),
        };
      })
      .filter(Boolean);

  const gradeOptions = metadataOptions?.gradeOptions ?? mapOptions(getMetaDataCollection(metaData, "Grade"), "grade_id", ["grade"]);
  const subjectOptions = metadataOptions?.subjectOptions ?? mapOptions(getMetaDataCollection(metaData, "Subject"), "subject_id", ["subject"]);
  const languageOptions = metadataOptions?.languageOptions ?? mapOptions(getMetaDataCollection(metaData, "Language"), "language_id", ["language"]);
  const examCategoryOptions = metadataOptions?.examCategoryOptions ?? mapOptions(getMetaDataCollection(metaData, "Exam_Category"), "exam_category_id", ["name"]);
  const termOptions = metadataOptions?.termOptions ?? mapOptions(getMetaDataCollection(metaData, "Term"), "term_id", ["term"]);
  const authorOptions = metadataOptions?.authorOptions ?? mapOptions(getMetaDataCollection(metaData, "Author"), "author_id", ["author"]);
  const sourceOptions = metadataOptions?.sourceOptions ?? mapOptions(getMetaDataCollection(metaData, "Source"), "source_id", ["source"]);
  const gradingMethodOptions = metadataOptions?.gradingMethodOptions ?? mapOptions(getMetaDataCollection(metaData, "Grading_Method"), "grading_method_id", ["method_name"]);
  const chapterOptions = metadataOptions?.chapterOptions ?? mapOptions(getMetaDataCollection(metaData, "Chapter"), "chapter_id", ["chapter"]);
  const yearOptions = metadataOptions?.yearOptions ?? mapOptions(getMetaDataCollection(metaData, "Year"), "year_id", ["year"]);
  const difficultyOptions = metadataOptions?.difficultyOptions ?? mapOptions(getMetaDataCollection(metaData, "Difficulty"), "difficulty_id", ["difficulty_type"]);
  const attemptMethodOptions = metadataOptions?.attemptMethodOptions ?? mapOptions(getMetaDataCollection(metaData, "Attempt_Method"), "attempt_no_id", ["no_of_attempts"]);
  const notationsOptions = metadataOptions?.notationsOptions ?? mapOptions(getMetaDataCollection(metaData, "Part_Notations"), "notation_id", ["notation_type"]);
  const termsAndConditionsOptions = metadataOptions?.termsAndConditionsOptions ?? getMetaDataCollection(metaData, "Terms_And_Conditions");
  const durationUnitOptions =
    metadataOptions?.durationUnitOptions ??
    metadataOptions?.unitOptions ??
    mapOptions(getMetaDataCollection(metaData, "units"), "unit_id", ["unit"]);


  useEffect(() => {
    const defaultUnitId = durationUnitOptions?.[0]?.value;

    if (!defaultUnitId) return;

    setSchedule((prev) => ({
      ...prev,
      exam_time_interval_unit_id:
        prev.exam_time_interval_unit_id || defaultUnitId,
      buffer_time_unit_id:
        prev.buffer_time_unit_id || defaultUnitId,
      pause_duration_unit_id:
        prev.pause_duration_unit_id || defaultUnitId,
    }));
  }, [durationUnitOptions]);

  const getTermsContentByLanguageId = (languageId) => {
    const normalizedLanguageId = Number(languageId);
    if (Number.isNaN(normalizedLanguageId)) {
      return "";
    }

    const matchedTerm = termsAndConditionsOptions.find(
      (item) => Number(item?.language_id) === normalizedLanguageId);
    return matchedTerm?.content || "";
  };

  const getOptionLabel = (options, value, fallback = "-") => {
    const match = (options || []).find(
      (option) => String(option.value) === String(value)
    );
    return match?.label || fallback;
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...(name === "language_id"
        ? {
          ...prev,
          [name]: value,
          terms_content: getTermsContentByLanguageId(value) || "",
        }
        : {
          ...prev,
          [name]: value,
        }),
    }));
    // clear field error
    setErrors((prev) => {
      const next = { ...prev };
      if (name in next) delete next[name];
      return next;
    });
  };

  //--------------------------

  //--------------------------
  // Form state handling
  //--------------------------
  const textInputHandler = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      if (name in next) delete next[name];
      return next;
    });
  };
  //--------------------------

  //--------------------------
  //date time handling
  //--------------------------

  const formatDateTimeForApi = (dateTimeLocalValue) => {
    if (!dateTimeLocalValue) {
      return "";
    }

    const [datePart, timePart] = String(dateTimeLocalValue).split("T");
    if (!datePart || !timePart) {
      return "";
    }

    const hhmm = timePart.slice(0, 5);
    return `${datePart} ${hhmm}:00`;
  };

  const formatDateTimeForInput = (dateTimeValue) => {
    if (!dateTimeValue) {
      return "";
    }

    const normalized = String(dateTimeValue).trim().replace(" ", "T");
    return normalized.length >= 16 ? normalized.slice(0, 16) : normalized;
  };

  const dateInputHandler = (event) => {
    const { name, value } = event.target;
    setSchedule((prev) => ({
      ...prev,
      [name]: name === "start_time" ? formatDateTimeForApi(value) : value,
    }));
  };

  const scheduleInputHandler = (event) => {
    const { name, value } = event.target;
    setSchedule((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const scheduleBooleanHandler = (name, value) => {
    setSchedule((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // const getMinutesMultiplierByUnit = (unitId) => {
  //   switch (String(unitId)) {
  //     case "2":
  //       return 1;
  //     case "3":
  //       return 60;
  //     case "4":
  //       return 1440;
  //     case "5":
  //       return 10080;
  //     case "6":
  //       return 43200;
  //     default:
  //       return 1;
  //   }
  // };

  const getUnitLabel = (unitId) => {
    const match = durationUnitOptions.find(
      (option) => String(option.value) === String(unitId)
    );

    return String(match?.label || "").toLowerCase();
  };

  const getMinutesMultiplierByUnit = (unitId) => {
    const unit = getUnitLabel(unitId);

    switch (unit) {
      case "hours":
      case "hour":
        return 60;

      case "days":
      case "day":
        return 24 * 60;

      case "weeks":
      case "week":
        return 7 * 24 * 60;

      case "months":
      case "month":
        return 30 * 24 * 60;

      case "minutes":
      case "minute":
      default:
        return 1;
    }
  };

  const toMinutes = (value, unitId) => {
    const parsed = Number(value);
    if (Number.isNaN(parsed) || parsed < 0) {
      return 0;
    }
    return Math.round(parsed * getMinutesMultiplierByUnit(unitId));
  };

  const toNullableInt = (value) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const toRequiredInt = (value, fallback) => {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  };

  const getValidationErrors = () => {
    const v = {};
    if (!String(formValues.title || "").trim()) {
      v.title = "Title is required";
    }
    if (!formValues.grade_id) {
      v.grade_id = "Grade is required";
    }
    if (!formValues.subject_id) {
      v.subject_id = "Subject is required";
    }
    if (!formValues.language_id) {
      v.language_id = "Language is required";
    }
    if (!formValues.exam_category_id) {
      v.exam_category_id = "Exam category is required";
    }
    if (!String(schedule.start_time || "").trim()) {
      v.start_time = "Start time is required";
    }
    return v;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isView || isSubmitting) {
      return;
    }
    const validationErrors = getValidationErrors();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      sweetErrorAlert("Missing fields", "Please fill required fields before creating.");
      return;
    }

    const createdBy = String(formValues.created_by || sessionUserId || "");
    const assessmentAdmin = String(
      formValues.assessment_admin || sessionUserId || "",
    );
    const useTimeLimit = Boolean(schedule.is_set_time_limit);

    const examTimeIntervalInMinutes = toMinutes(
      schedule.exam_time_interval,
      schedule.exam_time_interval_unit_id,
    );
    const bufferTimeInMinutes = toMinutes(
      schedule.buffer_time,
      schedule.buffer_time_unit_id,
    );
    const pauseDurationInMinutes = toMinutes(
      schedule.pause_duration,
      schedule.pause_duration_unit_id,
    );

    const { ismaster, ...cleanFormValues } = formValues;
    const assessmentPayload = {
      ...cleanFormValues,
      created_by: createdBy,
      assessment_admin: assessmentAdmin,
      attempt_no_id: toRequiredInt(formValues.attempt_no_id, 1),
      grading_method_id: toRequiredInt(formValues.grading_method_id, 1),
      privilege_id: toRequiredInt(formValues.privilege_id, 1),
      status_id: toRequiredInt(formValues.status_id, 1),
      language_id: toNullableInt(formValues.language_id),
      difficulty_id: toNullableInt(formValues.difficulty_id),
      category_id: toNullableInt(formValues.category_id),
      grade_id: toNullableInt(formValues.grade_id),
      author_id: toNullableInt(formValues.author_id),
      chapter_id: toNullableInt(formValues.chapter_id),
      source_id: toNullableInt(formValues.source_id),
      subject_id: toNullableInt(formValues.subject_id),
      term_id: toNullableInt(formValues.term_id),
      year_id: toNullableInt(formValues.year_id),
      exam_category_id: toNullableInt(formValues.exam_category_id),
      template_id: toNullableInt(formValues.template_id),
      no_of_parts: toNullableInt(formValues.no_of_parts),
      part_notation_id: toNullableInt(formValues.part_notation_id),
      parent_assessment_id: toNullableInt(formValues.parent_assessment_id),
      is_pause: Boolean(formValues.is_pause),
      pause_until: Boolean(formValues.is_pause) ? pauseDurationInMinutes : 0,
      is_restart: Boolean(formValues.is_restart),
      is_multiple: Boolean(formValues.is_multiple),
      ismaster: Boolean(formValues.ismaster),
      proctor_requirement: Boolean(formValues.proctor_requirement),
    };

    const schedulePayload = {
      start_time: schedule.start_time,
      exam_time_interval: useTimeLimit ? examTimeIntervalInMinutes : null,
      buffer_time: useTimeLimit ? bufferTimeInMinutes : null,
      scheduled_by: String(schedule.scheduled_by || createdBy),
      exam_time_interval_unit_id: useTimeLimit
        ? toRequiredInt(schedule.exam_time_interval_unit_id, 2)
        : null,
      buffer_time_unit_id: useTimeLimit
        ? toRequiredInt(schedule.buffer_time_unit_id, 2)
        : null,
      is_set_time_limit: useTimeLimit,
    };

    const assessorsPayload = [
      {
        user_id: String(assessors.user_id || assessmentAdmin),
        chief_assessor: Boolean(assessors.chief_assessor),
        assigned_by: String(assessors.assigned_by || createdBy),
      },
    ].filter((item) => String(item.user_id || "").trim());

    const userRolePayload = assessmentAdmin
      ? {
        user_id: assessmentAdmin,
        assigned_by: createdBy,
        role_code: "ASAD",
      }
      : null;

    const additionalUserRolesPayload = [
      {
        user_id: String(userRoles.user_id || ""),
        role_code: String(userRoles.role_code || "").trim(),
        assigned_by: String(userRoles.assigned_by || createdBy),
      },
    ].filter((item) => {
      if (!item.user_id || !item.role_code) {
        return false;
      }

      return !(
        userRolePayload &&
        item.user_id === userRolePayload.user_id &&
        item.role_code === userRolePayload.role_code
      );
    });

    const payload = {
      assessment: assessmentPayload,
      schedule: schedulePayload,
      user_role: userRolePayload,
      assessors: assessorsPayload,
      user_roles: additionalUserRolesPayload,
    };

    try {
      setIsSubmitting(true);
      if (typeof onSubmit === "function") {
        await onSubmit(payload);
      } else if (mode === "edit") {
        await assessSectionsService.updateAssessment(values.assessment_id, payload);
      } else {
        await assessSectionsService.createAssessment(payload);
      }
      const successTitle = mode === "edit" ? "Assessment updated" : "Assessment created";
      const successText =
        mode === "edit"
          ? "Assessment updated successfully"
          : "Assessment created successfully";
      await sweetSuccessAlert(successTitle, successText);
      if (typeof onClose === "function") {
        onClose();
      }
    } catch (error) {
      await sweetErrorAlert("Failed to submit assessment", "Please try again.");
      console.error("Failed to submit assessment", error);
    } finally {
      setIsSubmitting(false);
    }
  };
  //--------------------------

  //--------------------------
  // master assessment handling
  //--------------------------
  const [masterAssessments, setMasterAssessments] = useState([]);

  const normalizeAssessmentList = (response) => {
    if (Array.isArray(response?.data?.assessments)) {
      return response.data.assessments;
    }
    if (Array.isArray(response?.data)) {
      return response.data;
    }
    if (Array.isArray(response?.assessments)) {
      return response.assessments;
    }
    return [];
  };

  useEffect(() => {
    const fetchMasterAssessments = async () => {
      try {
        const response = await assessSectionsService.getMasterAssessmentsWithFilters({
          selectedGrades: formValues.grade_id ? [formValues.grade_id] : null,
          selectedLanguages: formValues.language_id ? [formValues.language_id] : null,
          selectedsubjects: formValues.subject_id ? [formValues.subject_id] : null,
        });
        setMasterAssessments(normalizeAssessmentList(response));
      } catch (error) {
        console.error("Failed to fetch master assessments", error);
      }
    };
    fetchMasterAssessments();
  }, [formValues.grade_id, formValues.language_id, formValues.subject_id]);

  //--------------------------


  const isFormValid = Object.keys(getValidationErrors()).length === 0;

  const summaryItems = [
    { label: "Title", value: formValues.title || "-", wide: true },
    { label: "Grade", value: getOptionLabel(gradeOptions, formValues.grade_id) },
    { label: "Subject", value: getOptionLabel(subjectOptions, formValues.subject_id) },
    { label: "Language", value: getOptionLabel(languageOptions, formValues.language_id) },
    { label: "Exam Category", value: getOptionLabel(examCategoryOptions, formValues.exam_category_id) },
    { label: "Term/ semester", value: getOptionLabel(termOptions, formValues.term_id) },
    { label: "Author", value: getOptionLabel(authorOptions, formValues.author_id) },
    { label: "Source", value: getOptionLabel(sourceOptions, formValues.source_id) },
    { label: "Grading Method", value: getOptionLabel(gradingMethodOptions, formValues.grading_method_id) },
    { label: "Chapter/ Module/ Unit", value: getOptionLabel(chapterOptions, formValues.chapter_id) },
    { label: "Year", value: getOptionLabel(yearOptions, formValues.year_id) },
    { label: "Difficulty", value: getOptionLabel(difficultyOptions, formValues.difficulty_id) },
    { label: "Attempts", value: getOptionLabel(attemptMethodOptions, formValues.attempt_no_id) },
  ];

  return (
    <form
      className={`assess-setup-form ${isView ? "assess-setup-form--view" : "assess-setup-form--edit"}`}
      onSubmit={handleSubmit}
    >
      {isMetaDataLoading && (
        <div className="assess-setup__loader">
          <SectionSpinner text="Loading..." />
        </div>
      )}

      {metaDataError && !isMetaDataLoading && (
        <p className="assess-setup__meta-error">
          Unable to load metadata. Showing available default options.
        </p>
      )}

      {isView ? (
        <>
          <div className="assess-summary">
            <div className="assess-summary__grid">
              {summaryItems.map((item) => (
                <div
                  key={item.label}
                  className={`assess-summary__item${item.wide ? " assess-summary__item--wide" : ""}`}
                >
                  <span className="assess-summary__label">{item.label}</span>
                  <span className="assess-summary__value">{item.value}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="assess-terms">
            <div className="assess-terms__header">
              <h3>Terms & Conditions</h3>
            </div>
            <div
              className={`assess-terms__body${termsExpanded ? "" : " is-collapsed"}`}
            >
              <TermsEditor
                value={formValues.terms_content || "No terms provided."}
                onChange={(val) =>
                  setFormValues((prev) => ({
                    ...prev,
                    terms_content: val,
                  }))
                }
                readOnly={true}
              />
            </div>
            <div className="assess-terms__footer">
              <button
                type="button"
                className={`assess-terms__toggle${termsExpanded ? " is-open" : ""}`}
                onClick={() => setTermsExpanded((prev) => !prev)}
              >
                {termsExpanded ? "Collapse" : "Expand"}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="assess-setup__grid assess-setup__grid--single-start">
            <TextField label="Title" required value={formValues.title} disabled={isFormDisabled} name="title" onChange={textInputHandler} error={errors.title} />
          </div>

          <div className="assess-setup__grid">
            <SelectField label="Grade" required name="grade_id" value={formValues.grade_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={gradeOptions} error={errors.grade_id} />
            <SelectField label="Subject" required name="subject_id" value={formValues.subject_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={subjectOptions} error={errors.subject_id} />
            <SelectField label="Language" required name="language_id" value={formValues.language_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={languageOptions} error={errors.language_id} />
            <SelectField label="Exam Category" required name="exam_category_id" value={formValues.exam_category_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={examCategoryOptions} error={errors.exam_category_id} />
            <SelectField label="Term/ semester" name="term_id" value={formValues.term_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={termOptions} />
            <SelectField label="Author" name="author_id" value={formValues.author_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={authorOptions} />
            <SelectField label="Source" name="source_id" value={formValues.source_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={sourceOptions} />
            <SelectField label="Grading Method" name="grading_method_id" value={formValues.grading_method_id || ""} onChange={handleInputChange} disabled={isFormDisabled} options={gradingMethodOptions} />
            <SelectField label="Chapter/ Module/ Unit" name="chapter_id" value={formValues.chapter_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={chapterOptions} />
            <SelectField label="Year" name="year_id" value={formValues.year_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={yearOptions} />
            <SelectField label="Difficulty" name="difficulty_id" value={formValues.difficulty_id || ""} onChange={handleInputChange} disabled={isFormDisabled} selectPlaceholder="Nothing selected" options={difficultyOptions} />
            <SelectField label="Attempts" name="attempt_no_id" value={formValues.attempt_no_id || ""} onChange={handleInputChange} disabled={isFormDisabled} options={attemptMethodOptions} />
          </div>

          <div className="assess-terms">
            <div className="assess-terms__header">
              <h3>Terms & Conditions</h3>
            </div>
            <div
              className={`assess-terms__body${termsExpanded ? "" : " is-collapsed"}`}
            >
              <TermsEditor
                value={formValues.terms_content || ""}
                onChange={(val) =>
                  setFormValues((prev) => ({
                    ...prev,
                    terms_content: val,
                  }))
                }
                readOnly={isFormDisabled}
              />
            </div>
            <div className="assess-terms__footer">
              <button
                type="button"
                className={`assess-terms__toggle${termsExpanded ? " is-open" : ""}`}
                onClick={() => setTermsExpanded((prev) => !prev)}
              >
                {termsExpanded ? "Collapse" : "Expand"}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="assess-setup__grid assess-setup__grid--compact">
        <SelectField label="Privilege" value={tempData.privilege_status} disabled={true} selectPlaceholder="Public" />
        <SelectField label="Status" value={formValues.assessment_state} disabled={true} selectPlaceholder="Initial" />
      </div>

      <div className="assess-setup__full">
        <label>Additional Information:</label>
        <textarea rows="5" value={formValues.additional_info || ""} disabled={isFormDisabled} name="additional_info" onChange={textInputHandler} />
      </div>

      <h2 className="assess-setup__section-title">Schedule Settings:</h2>
      <div className="assess-setup__schedule">
        <label>Assessment Date and Time (Timezone: Asia/Colombo)</label>
        <div>
          <input
            type="datetime-local"
            value={formatDateTimeForInput(schedule.start_time)}
            disabled={isFormDisabled}
            name="start_time"
            onChange={dateInputHandler}
          />
          <p>Click calendar icon to schedule assessment</p>
          {errors.start_time && (
            <div className="assess-setup__field-error">{errors.start_time}</div>
          )}
        </div>
      </div>

      <CheckboxPair
        label="Do you want to set a time limit?"
        name="is_set_time_limit"
        value={Boolean(schedule.is_set_time_limit)}
        onChange={(nextValue) =>
          scheduleBooleanHandler("is_set_time_limit", nextValue)
        }
        disabled={isFormDisabled}
      />

      {Boolean(schedule.is_set_time_limit) && (
        <div className="assess-setup__time-limit-panel">
          <div className="assess-setup__duration-row">
            <label>Time Duration</label>
            <div className="assess-setup__duration-inputs">
              <input
                type="number"
                min="1"
                name="exam_time_interval"
                value={schedule.exam_time_interval || ""}
                onChange={scheduleInputHandler}
                disabled={isFormDisabled}
                placeholder="Enter the time duration in selected unit"
              />
              <select
                name="exam_time_interval_unit_id"
                value={schedule.exam_time_interval_unit_id}
                onChange={scheduleInputHandler}
                disabled={isFormDisabled}
              >
                {durationUnitOptions.map((option) => (
                  <option key={`exam-unit-${option.value}`} value={option.value} >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="assess-setup__duration-row">
            <label>Access Buffer Duration</label>
            <div className="assess-setup__duration-inputs">
              <input
                type="number"
                min="0"
                name="buffer_time"
                value={schedule.buffer_time || ""}
                onChange={scheduleInputHandler}
                disabled={isFormDisabled}
                placeholder="Enter the buffer time in selected unit"
              />
              <select
                name="buffer_time_unit_id"
                value={schedule.buffer_time_unit_id}
                onChange={scheduleInputHandler}
                disabled={isFormDisabled}
              >
                {durationUnitOptions.map((option) => (
                  <option
                    key={`buffer-unit-${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <CheckboxPair
            label="Pause Option"
            name="is_pause"
            value={Boolean(formValues.is_pause)}
            onChange={(nextValue) =>
              setFormValues((prev) => ({
                ...prev,
                is_pause: nextValue,
              }))
            }
            disabled={isFormDisabled}
          />

          {Boolean(formValues.is_pause) && (
            <div className="assess-setup__time-limit-panel">
              <div className="assess-setup__duration-row">
                <label>Pause Duration</label>
                <div className="assess-setup__duration-inputs">
                  <input
                    type="number"
                    min="1"
                    name="pause_duration"
                    value={schedule.pause_duration || ""}
                    onChange={scheduleInputHandler}
                    disabled={isFormDisabled}
                    placeholder="Enter pause duration"
                  />
                  <select
                    name="pause_duration_unit_id"
                    value={schedule.pause_duration_unit_id}
                    onChange={scheduleInputHandler}
                    disabled={isFormDisabled}
                  >
                    {durationUnitOptions.map((option) => (
                      <option key={`pause-unit-${option.value}`} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      )}



      <CheckboxPair
        label="Restart Option"
        name="is_restart"
        value={Boolean(formValues.is_restart)}
        onChange={(nextValue) =>
          setFormValues((prev) => ({
            ...prev,
            is_restart: nextValue,
          }))
        }
        disabled={isFormDisabled}
      />

      <h2 className="assess-setup__section-title">Template Settings:</h2>
      <div className="assess-setup__grid assess-setup__grid--template">
        <TextField
          label="No of Parts"
          name="no_of_parts"
          value={formValues.no_of_parts}
          onChange={textInputHandler}
          disabled={isFormDisabled}
        />
        <SelectField
          label="Part Notation"
          name="part_notation_id"
          value={formValues.part_notation_id}
          onChange={handleInputChange}
          disabled={isFormDisabled}
          options={notationsOptions}
        />
      </div>

      <h2 className="assess-setup__section-title">Single/Multiple Paper:</h2>
      <CheckboxPair
        label="Does this have multiple papers?"
        name="is_multiple"
        value={Boolean(formValues.is_multiple)}
        onChange={(nextValue) =>
          setFormValues((prev) => ({
            ...prev,
            is_multiple: nextValue,
            ismaster: nextValue ? prev.ismaster : true,
            parent_assessment_id: nextValue ? prev.parent_assessment_id : null,
          }))
        }
        disabled={isFormDisabled}
      />

      {Boolean(formValues.is_multiple) && (
        <>
          <CheckboxPair
            label="Is this the master paper?"
            name="ismaster"
            value={Boolean(formValues.ismaster)}
            onChange={(nextValue) =>
              setFormValues((prev) => ({
                ...prev,
                ismaster: nextValue,
                parent_assessment_id: nextValue ? null : prev.parent_assessment_id,
              }))
            }
            disabled={isFormDisabled}
          />

          {!Boolean(formValues.ismaster) && (
            <div className="assess-setup__field">
              <label>Search Master Paper...</label>
              <select
                name="parent_assessment_id"
                value={formValues.parent_assessment_id || ""}
                onChange={handleInputChange}
                disabled={isFormDisabled}
              >
                <option value="">Select a master paper...</option>
                {masterAssessments.map((assessment) => {
                  const assessmentId = assessment.assessment_id ?? assessment.id ?? "";
                  const assessmentTitle =
                    assessment.title ??
                    assessment.name ??
                    assessment.assessment_title ??
                    `ASMNT${assessmentId}`;

                  return (
                    <option key={assessmentId} value={assessmentId}>
                      {assessmentTitle}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </>
      )}

      <div className="assess-setup__actions">
        {!isView && (
          <>
            <SaveButton
              type="submit"
              label={isSubmitting ? "Creating..." : submitLabel}
              disabled={isMetaDataLoading || isSubmitting || !isFormValid}
            />

          </>
        )}
        <CloseButton label="Close" onClick={onClose} />
      </div>
    </form>
  );
}
